/**
 * ProfilePage - User profile management and settings.
 * Allows users to view and update their profile information and preferences.
 *
 * Feature 013: Includes physical profile (height, weight, targets) and BMI calculation
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import type { PhysicalProfile } from '../types';

// Type guard to safely access physical_profile
type UserWithProfile = {
  profile: {
    physical_profile?: PhysicalProfile;
    [key: string]: any;
  };
  [key: string]: any;
};

const LIFE_STAGES = [
  { value: 'perimenopause', label: 'Perimenopause' },
  { value: 'menopause', label: 'Menopause' },
  { value: 'post-menopause', label: 'Post-Menopause' },
];

const PRIMARY_GOALS = [
  { value: 'lose_weight', label: 'Lose Weight' },
  { value: 'lose_belly_fat', label: 'Lose Belly Fat' },
  { value: 'build_strength', label: 'Build Strength' },
  { value: 'improve_energy', label: 'Improve Energy' },
];

// BMI category colors
const BMI_CATEGORIES = {
  underweight: { label: 'Underweight', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  normal: { label: 'Normal', color: 'bg-green-100 text-green-800 border-green-200' },
  overweight: { label: 'Overweight', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  obese: { label: 'Obese', color: 'bg-red-100 text-red-800 border-red-200' },
};

const ProfilePage: React.FC = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Personal details form state
  const [firstName, setFirstName] = useState(user?.profile?.first_name || '');
  const [lastName, setLastName] = useState(user?.profile?.last_name || '');
  const [dateOfBirth, setDateOfBirth] = useState(user?.profile?.date_of_birth || '');

  // Wellness profile form state
  const [lifeStage, setLifeStage] = useState(user?.profile.life_stage || 'perimenopause');
  const [primaryGoal, setPrimaryGoal] = useState(user?.profile.primary_goal || 'improve_energy');
  const [dailyReminder, setDailyReminder] = useState(user?.settings?.notification_preferences.daily_reminder ?? true);
  const [reminderTime, setReminderTime] = useState(user?.settings?.notification_preferences.reminder_time || '08:00');

  // Physical profile state (US4 - T056, T058)
  const [isEditingPhysical, setIsEditingPhysical] = useState(false);
  const userWithProfile = user as UserWithProfile | null;
  const physicalProfile = userWithProfile?.profile?.physical_profile;
  const [height, setHeight] = useState(physicalProfile?.height?.value?.toString() || '');
  const [weight, setWeight] = useState(physicalProfile?.weight?.value?.toString() || '');
  const [targetWeight, setTargetWeight] = useState(physicalProfile?.target_weight?.value?.toString() || '');
  const [targetWaist, setTargetWaist] = useState(physicalProfile?.target_waist?.value?.toString() || '');
  const [bmi, setBmi] = useState<number | null>(null);
  const [bmiCategory, setBmiCategory] = useState<string | null>(null);

  // Fetch profile with BMI on mount and after updates
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await apiClient.get('/profile');
        const profileData = response.data;

        // Update BMI if available
        if (profileData.calculated_metrics) {
          setBmi(profileData.calculated_metrics.bmi);
          setBmiCategory(profileData.calculated_metrics.bmi_category);
        }
      } catch (err) {
        console.error('Failed to fetch profile:', err);
      }
    };

    fetchProfile();
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError('');
      setSuccess('');

      // Validate personal details
      if (firstName.trim() && firstName.trim().length > 50) {
        setError('First name must be 50 characters or less');
        setIsSaving(false);
        return;
      }

      if (lastName.trim() && lastName.trim().length > 50) {
        setError('Last name must be 50 characters or less');
        setIsSaving(false);
        return;
      }

      if (dateOfBirth) {
        const dob = new Date(dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
          age--;
        }
        if (age < 18) {
          setError('You must be at least 18 years old');
          setIsSaving(false);
          return;
        }
      }

      // Build physical profile if height and weight provided
      let physicalProfileUpdate: Partial<PhysicalProfile> | null = null;
      if (height && weight) {
        physicalProfileUpdate = {
          height: { value: parseFloat(height), unit: 'cm' },
          weight: { value: parseFloat(weight), unit: 'kg' },
          preferred_unit_system: 'metric'
        };

        if (targetWeight) {
          physicalProfileUpdate.target_weight = { value: parseFloat(targetWeight), unit: 'kg' };
        }
        if (targetWaist) {
          physicalProfileUpdate.target_waist = { value: parseFloat(targetWaist), unit: 'cm' };
        }
      }

      const response = await apiClient.patch('/profile', {
        profile: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          date_of_birth: dateOfBirth,
          life_stage: lifeStage,
          primary_goal: primaryGoal,
          ...(physicalProfileUpdate && { physical_profile: physicalProfileUpdate }),
        },
        settings: {
          notification_preferences: {
            daily_reminder: dailyReminder,
            reminder_time: reminderTime,
          },
        },
      });

      const updatedUser = response.data;
      if (updateUser && updatedUser) {
        updateUser(updatedUser);
      }

      // Refresh BMI if available (US4 - T059)
      const profileResponse = await apiClient.get('/profile');
      if (profileResponse.data.calculated_metrics) {
        setBmi(profileResponse.data.calculated_metrics.bmi);
        setBmiCategory(profileResponse.data.calculated_metrics.bmi_category);
      }

      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      setIsEditingPhysical(false);

      // Refresh user data
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      setError('');

      await apiClient.delete('/profile');

      // Logout and redirect to auth page
      await logout();
      navigate('/auth');
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setFirstName(user?.profile?.first_name || '');
    setLastName(user?.profile?.last_name || '');
    setDateOfBirth(user?.profile?.date_of_birth || '');
    setLifeStage(user?.profile.life_stage || 'perimenopause');
    setPrimaryGoal(user?.profile.primary_goal || 'improve_energy');
    setDailyReminder(user?.settings?.notification_preferences.daily_reminder ?? true);
    setReminderTime(user?.settings?.notification_preferences.reminder_time || '08:00');

    // Reset physical profile
    const resetUserWithProfile = user as UserWithProfile | null;
    const userPhysicalProfile = resetUserWithProfile?.profile?.physical_profile;
    setHeight(userPhysicalProfile?.height?.value?.toString() || '');
    setWeight(userPhysicalProfile?.weight?.value?.toString() || '');
    setTargetWeight(userPhysicalProfile?.target_weight?.value?.toString() || '');
    setTargetWaist(userPhysicalProfile?.target_waist?.value?.toString() || '');

    setIsEditing(false);
    setIsEditingPhysical(false);
    setError('');
  };

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      setIsExporting(true);
      setError('');

      const response = await apiClient.get(`/export?format=${format}`, {
        responseType: 'blob',
      });

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `agestrong_data.${format}`;

      // Download the file
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess(`Data exported successfully as ${format.toUpperCase()}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="mb-10">
        <h1 className="text-4xl font-serif font-bold text-primary-800 mb-3">
          Profile & Settings
        </h1>
        <p className="text-neutral-600">
          Manage your account information and preferences
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-400 rounded-r-lg animate-slide-down">
          <p className="text-sm text-green-800 font-medium">{success}</p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
          <p className="text-sm text-red-800 font-medium">{error}</p>
        </div>
      )}

      {/* Account Information */}
      <div className="card shadow-elegant mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-serif font-bold text-neutral-900">Account Information</h2>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="btn btn-outline text-sm px-5 py-2"
            >
              Edit Profile
            </button>
          )}
        </div>

        <div className="space-y-6">
          {/* Email (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="input bg-neutral-50 cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-neutral-500">Email cannot be changed</p>
          </div>

          {/* Personal Details */}
          <div className="border-t border-primary-200 pt-6">
            <h3 className="text-lg font-serif font-semibold text-neutral-800 mb-4">
              Personal Details
            </h3>

            <div className="space-y-4">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={!isEditing}
                  className="input disabled:bg-neutral-50 disabled:cursor-not-allowed"
                  placeholder="Enter your first name"
                  maxLength={50}
                  autoComplete="given-name"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={!isEditing}
                  className="input disabled:bg-neutral-50 disabled:cursor-not-allowed"
                  placeholder="Enter your last name"
                  maxLength={50}
                  autoComplete="family-name"
                />
              </div>

              {/* Date of Birth */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  disabled={!isEditing}
                  className="input disabled:bg-neutral-50 disabled:cursor-not-allowed"
                  autoComplete="bday"
                />
              </div>
            </div>
          </div>

          {/* Life Stage */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Life Stage
            </label>
            <select
              value={lifeStage}
              onChange={(e) => setLifeStage(e.target.value)}
              disabled={!isEditing}
              className="input disabled:bg-neutral-50 disabled:cursor-not-allowed"
            >
              {LIFE_STAGES.map((stage) => (
                <option key={stage.value} value={stage.value}>
                  {stage.label}
                </option>
              ))}
            </select>
          </div>

          {/* Primary Goal */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Primary Goal
            </label>
            <select
              value={primaryGoal}
              onChange={(e) => setPrimaryGoal(e.target.value)}
              disabled={!isEditing}
              className="input disabled:bg-neutral-50 disabled:cursor-not-allowed"
            >
              {PRIMARY_GOALS.map((goal) => (
                <option key={goal.value} value={goal.value}>
                  {goal.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isEditing && (
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-primary-200">
            <button
              onClick={handleCancel}
              className="btn btn-outline px-6 py-3"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn btn-primary px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Physical Profile - US4 T056, T057 */}
      <div className="card shadow-elegant mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-serif font-bold text-neutral-900">Body Metrics</h2>
          {!isEditingPhysical && (
            <button
              onClick={() => setIsEditingPhysical(true)}
              className="btn btn-outline text-sm px-5 py-2"
            >
              {height || weight ? 'Edit Metrics' : 'Add Metrics'}
            </button>
          )}
        </div>

        {/* BMI Badge - US4 T057 */}
        {bmi && bmiCategory && (
          <div className="mb-6 p-4 bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg border border-primary-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600 mb-1">Your BMI</p>
                <p className="text-3xl font-serif font-bold text-primary-800">{bmi}</p>
              </div>
              <div>
                <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium border ${BMI_CATEGORIES[bmiCategory as keyof typeof BMI_CATEGORIES]?.color || 'bg-gray-100'}`}>
                  {BMI_CATEGORIES[bmiCategory as keyof typeof BMI_CATEGORIES]?.label || bmiCategory}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Height */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Height (cm)
            </label>
            <input
              type="number"
              step="0.1"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              disabled={!isEditingPhysical}
              className="input disabled:bg-neutral-50 disabled:cursor-not-allowed"
              placeholder="e.g., 165"
            />
          </div>

          {/* Weight */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Weight (kg)
            </label>
            <input
              type="number"
              step="0.1"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              disabled={!isEditingPhysical}
              className="input disabled:bg-neutral-50 disabled:cursor-not-allowed"
              placeholder="e.g., 70"
            />
          </div>

          {/* Targets */}
          {(height && weight) && (
            <div className="border-t border-primary-200 pt-6">
              <h3 className="text-lg font-serif font-semibold text-neutral-800 mb-4">
                Your Goals <span className="text-neutral-400 font-normal text-sm">(optional)</span>
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Target Weight Loss (kg)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={targetWeight}
                    onChange={(e) => setTargetWeight(e.target.value)}
                    disabled={!isEditingPhysical}
                    className="input disabled:bg-neutral-50 disabled:cursor-not-allowed"
                    placeholder="e.g., 5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Target Waist Reduction (cm)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={targetWaist}
                    onChange={(e) => setTargetWaist(e.target.value)}
                    disabled={!isEditingPhysical}
                    className="input disabled:bg-neutral-50 disabled:cursor-not-allowed"
                    placeholder="e.g., 8"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Empty state - US4 T060 */}
          {!height && !weight && !isEditingPhysical && (
            <div className="text-center py-8 bg-primary-50 rounded-lg border-2 border-dashed border-primary-200">
              <p className="text-neutral-600 mb-2">No body metrics recorded yet</p>
              <p className="text-sm text-neutral-500">Click "Add Metrics" to get started</p>
            </div>
          )}
        </div>

        {isEditingPhysical && (
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-primary-200">
            <button
              onClick={() => {
                setIsEditingPhysical(false);
                handleCancel();
              }}
              className="btn btn-outline px-6 py-3"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn btn-primary px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Metrics'}
            </button>
          </div>
        )}
      </div>

      {/* Notification Settings */}
      <div className="card shadow-elegant mb-8">
        <h2 className="text-2xl font-serif font-bold text-neutral-900 mb-6">
          Notification Settings
        </h2>

        <div className="space-y-6">
          {/* Daily Reminder Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-neutral-700">
                Daily Check-In Reminder
              </label>
              <p className="text-xs text-neutral-500 mt-1">
                Receive a daily reminder to complete your morning check-in
              </p>
            </div>
            <button
              type="button"
              onClick={() => isEditing && setDailyReminder(!dailyReminder)}
              disabled={!isEditing}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                dailyReminder ? 'bg-primary-600' : 'bg-neutral-300'
              } ${!isEditing ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  dailyReminder ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Reminder Time */}
          {dailyReminder && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Reminder Time
              </label>
              <input
                type="time"
                value={reminderTime}
                onChange={(e) => setReminderTime(e.target.value)}
                disabled={!isEditing}
                className="input disabled:bg-neutral-50 disabled:cursor-not-allowed"
              />
            </div>
          )}
        </div>
      </div>

      {/* Data Export */}
      <div className="card shadow-elegant mb-8">
        <h2 className="text-2xl font-serif font-bold text-neutral-900 mb-4">
          Data Export
        </h2>
        <p className="text-sm text-neutral-600 mb-6">
          Download all your data for backup or portability. This includes your profile information and all check-in history.
        </p>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => handleExport('json')}
            disabled={isExporting}
            className="btn btn-outline px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? 'Exporting...' : 'Export as JSON'}
          </button>
          <button
            onClick={() => handleExport('csv')}
            disabled={isExporting}
            className="btn btn-outline px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? 'Exporting...' : 'Export as CSV'}
          </button>
        </div>

        <div className="mt-4 p-4 bg-primary-50 rounded-lg">
          <p className="text-xs text-neutral-600">
            <strong>JSON:</strong> Complete data with full structure (recommended for backup)<br />
            <strong>CSV:</strong> Flattened check-in data, suitable for spreadsheet analysis
          </p>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="card border-2 border-red-200 bg-red-50">
        <h2 className="text-xl font-serif font-bold text-red-900 mb-4">
          Danger Zone
        </h2>
        <p className="text-sm text-red-800 mb-6">
          Once you delete your account, there is no going back. This action is permanent and will delete all your data.
        </p>

        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn bg-red-600 text-white hover:bg-red-700 px-6 py-3"
          >
            Delete Account
          </button>
        ) : (
          <div className="space-y-4">
            <p className="text-sm font-medium text-red-900">
              Are you absolutely sure? This cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-outline px-6 py-3"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="btn bg-red-600 text-white hover:bg-red-700 px-6 py-3 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete My Account'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
